// Web pageview auto-capture (Tier 2).
//
// Mirrors PostHog's pageview heuristic:
//  - Emits `page_viewed` on install, on `popstate`, on a bfcache restore
//    (`pageshow` with `persisted`), and on monkey-patched
//    `history.pushState` / `history.replaceState`. A `pushState` counts on
//    any URL change; a `replaceState` counts only when the path or hash
//    changed, because routers and search boxes mirror state into the query
//    string on every keystroke and that is still the same page.
//  - Emits `page_left` on `pagehide` / `visibilitychange:hidden`, with
//    `time_on_page_ms` measured from the matching `page_viewed`. The
//    install-time page view is anchored to navigation start, so a visitor
//    who leaves during SDK init is still counted. When the tab becomes
//    visible again the same page view re-opens under the same
//    `pageview_id`, so the next `page_left` covers the new segment; a
//    consumer sums `time_on_page_ms` per `pageview_id`. A `beforeunload` the
//    user cancels re-opens the view the same way.
//  - Generates a `pageview_id` (UUID v4) per pageview so both events can
//    be joined server-side. Stays stable across History API mutations
//    that the user perceives as "still the same page" (we mint a fresh id
//    on URL change). The handle exposes the current id so session replay
//    chunks can carry it.
//  - Generates a `window_id` per tab, persisted via `sessionStorage` so
//    it survives a soft reload but not a new tab. (Falls back to a fresh
//    id when sessionStorage is unavailable.)
//  - Emits `$deep_link_opened` once on init when the page was loaded
//    with no referrer AND the URL carries an attribution signal
//    (UTM params, gclid, fbclid, msclkid, ttclid, etc.).
//  - Sets initial-touch super-properties (set-once) on first call:
//    initial_referrer, initial_referring_domain, initial_utm_*,
//    initial_gclid / fbclid / msclkid / ttclid, initial_landing_path.
//
// Ownership: this module is browser-only. It uses `window`, `document`,
// `history`, `sessionStorage`, `location`. It is imported only from
// `@layers/client` which already gates on `typeof window`.
//
// One install per window: a marker on `window` makes a second install a
// no-op handle, so two clients on one page (two script tags, two hoisted
// copies of the package) cannot wrap the History API twice and double
// every event. Teardown restores a History method only while our wrapper
// is still the one installed; a wrapper another tool put on top of ours
// stays, and ours goes passive underneath it.
import type { EventProperties } from '@layers/core-wasm';

import { generateUUID } from './uuid.js';

/** Minimal tracker surface — supplied by LayersClient at install time. */
export interface PageviewClientHooks {
  /** Track an event. */
  track(eventName: string, properties?: EventProperties): void;
  /** Set super-properties with set-once semantics. */
  setSuperPropertiesOnce(properties: Record<string, unknown>): void;
  /** Whether the SDK should emit debug logs. */
  enableDebug?: boolean;
}

/** Configuration for `installPageviewTracking`. */
export interface PageviewTrackingOptions {
  /**
   * Whether to emit `page_viewed` on install (and on every URL change).
   * @default true
   */
  trackPageview?: boolean;
  /**
   * Whether to emit `page_left` on `pagehide` / `visibilitychange:hidden`.
   * @default true
   */
  trackPageleave?: boolean;
  /**
   * Whether to capture initial-touch super-properties (referrer, UTM, click IDs).
   * @default true
   */
  captureInitialTouch?: boolean;
  /**
   * Whether to emit `$deep_link_opened` on initial load when the URL carries
   * an attribution signal AND `document.referrer` is empty (suggests the
   * tab was opened from outside the browser — e.g. via a deep link or
   * paid ad click).
   * @default true
   */
  trackDeepLinkOpened?: boolean;
}

/** Return value of `installPageviewTracking` — call to remove listeners. */
export type PageviewTrackingHandle = {
  /**
   * Emit the `page_left` for the open page view, tear down all listeners,
   * and restore the patched History methods that are still ours.
   */
  shutdown(): void;
  /** Manually emit a pageview (e.g. for SPA frameworks bypassing History API). */
  capturePageview(): void;
  /**
   * Emit the `page_left` for the open page view now, if one is open. The
   * client calls this from its own hide / unload handlers before it flushes,
   * so the leave event rides the same beacon as everything queued before it.
   * Idempotent: a second call before the next page view does nothing.
   */
  capturePageleave(): void;
  /**
   * The `pageview_id` other data (session replay chunks) should join to: the
   * open page view, or the one just left until a new page view starts.
   * `undefined` before the first page view and after `shutdown()`.
   */
  currentPageviewId(): string | undefined;
};

export type PageviewReason =
  | 'init'
  | 'popstate'
  | 'pushState'
  | 'replaceState'
  | 'bfcache'
  | 'manual';

const SESSION_STORAGE_WINDOW_ID_KEY = '__layers_window_id';
/** Set on `window` while an install is live; a second install is a no-op. */
const INSTALL_MARKER = '__layersPageviewTrackingInstalled';
// History API mutators we monkey-patch. Stored as a single tuple so the
// teardown logic can restore them atomically.
const HISTORY_PATCH_METHODS = ['pushState', 'replaceState'] as const;
type HistoryMethod = (typeof HISTORY_PATCH_METHODS)[number];

// Attribution params that flag a "deep link" landing in the absence of a
// referrer. Also drive the initial-touch super-properties. Keep this list in
// sync with `attribution.ts` and the node SDK's `CLICK_ID_PARAMS` so all
// platforms agree on which params count as paid acquisition signals.
const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'dclid',
  'gbraid',
  'wbraid',
  'twclid',
  'li_fat_id',
  'rclid'
] as const;

interface PageviewState {
  pageviewId: string;
  /** Absolute href at the time the pageview was emitted — reported on
   *  the paired `page_left` so the URL field matches the `page_viewed`. */
  url: string;
  /** Performance.now() snapshot for time_on_page calculation. */
  startedAtMs: number;
}

const NOOP_HANDLE: PageviewTrackingHandle = {
  shutdown() {
    /* no-op */
  },
  capturePageview() {
    /* no-op */
  },
  capturePageleave() {
    /* no-op */
  },
  currentPageviewId() {
    return undefined;
  }
};

/**
 * Install web pageview / pageleave / deep-link / initial-touch tracking.
 *
 * Returns a handle whose `shutdown()` emits the pending `page_left`,
 * removes all listeners, and restores the History methods that are still
 * ours. One install per window: while a handle is live, a further install
 * on the same window returns a no-op handle.
 */
export function installPageviewTracking(
  client: PageviewClientHooks,
  options: PageviewTrackingOptions = {}
): PageviewTrackingHandle {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return NOOP_HANDLE;
  }
  // Some test environments stub `window` without a full `history` object.
  // Bail gracefully so the SDK still works; the consumer can call
  // `installPageviewTracking` again after the environment is fully
  // initialized, or rely on `track('page_viewed')` manually.
  if (typeof window.history === 'undefined') {
    return NOOP_HANDLE;
  }
  const marked = window as unknown as Record<string, unknown>;
  if (marked[INSTALL_MARKER] === true) {
    if (client.enableDebug) {
      console.warn('[Layers] page view tracking is already installed on this window');
    }
    return NOOP_HANDLE;
  }
  marked[INSTALL_MARKER] = true;

  const trackPageview = options.trackPageview !== false;
  const trackPageleave = options.trackPageleave !== false;
  const captureInitialTouch = options.captureInitialTouch !== false;
  const trackDeepLinkOpened = options.trackDeepLinkOpened !== false;

  const windowId = getOrCreateWindowId();
  let current: PageviewState | null = null;
  /** The last page view, kept so a hidden→visible tab can re-open it. */
  let last: PageviewState | null = null;
  let previousUrl: string | null = null;
  /** Cleared by shutdown(); the History wrappers go passive after that. */
  let active = true;

  // Capture the original History methods so shutdown() can restore them
  // exactly, and our wrappers so it restores only what is still ours.
  type HistoryFn = (...args: unknown[]) => unknown;
  const originalHistory: Partial<Record<HistoryMethod, HistoryFn>> = {};
  const patchedHistory: Partial<Record<HistoryMethod, HistoryFn>> = {};

  // ── Initial-touch super-properties ─────────────────────────────────
  if (captureInitialTouch) {
    try {
      const params = parseQueryParams(window.location.search);
      const referrer = document.referrer || null;
      const initial: Record<string, unknown> = {
        initial_referrer: referrer,
        initial_referring_domain: referrer ? safeUrlHost(referrer) : null,
        initial_landing_path: window.location.pathname || '/'
      };
      for (const key of ATTRIBUTION_PARAM_KEYS) {
        if (params[key]) {
          initial[`initial_${key}`] = params[key];
        }
      }
      client.setSuperPropertiesOnce(initial);
      if (client.enableDebug) {
        console.log('[Layers] initial-touch super-properties registered:', initial);
      }
    } catch (e) {
      if (client.enableDebug) console.warn('[Layers] initial-touch capture failed:', e);
    }
  }

  // ── $deep_link_opened heuristic ────────────────────────────────────
  if (trackDeepLinkOpened) {
    try {
      const referrer = document.referrer || '';
      const params = parseQueryParams(window.location.search);
      const hasAttribution = ATTRIBUTION_PARAM_KEYS.some((k) => params[k]);
      if (referrer === '' && hasAttribution) {
        const properties: Record<string, unknown> = {
          ...params,
          url: window.location.href,
          path: window.location.pathname,
          window_id: windowId
        };
        client.track('$deep_link_opened', properties);
        if (client.enableDebug) {
          console.log('[Layers] $deep_link_opened (no referrer + attribution)');
        }
      }
    } catch (e) {
      if (client.enableDebug) console.warn('[Layers] $deep_link_opened heuristic failed:', e);
    }
  }

  // ── Pageview emit helper ───────────────────────────────────────────
  function emitPageview(reason: PageviewReason) {
    if (!trackPageview) return;
    try {
      // If a previous pageview is open, emit pageleave first so the pair
      // is balanced (PostHog's behavior — every pageview gets a leave).
      flushPageleaveIfOpen();
      const id = generateUUID();
      // The install-time page view began at navigation start, which is
      // where the high-resolution clock's zero sits. Anchoring there keeps
      // the time a visitor spent while the SDK was still loading.
      const startedAtMs = reason === 'init' && hasHighResClock() ? 0 : nowMs();
      current = {
        pageviewId: id,
        url: window.location.href,
        startedAtMs
      };
      last = current;
      const props: Record<string, unknown> = {
        pageview_id: id,
        window_id: windowId,
        url: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        title: document.title,
        referrer: document.referrer || null,
        previous_url: previousUrl,
        reason
      };
      previousUrl = window.location.href;
      client.track('page_viewed', props);
      if (client.enableDebug) {
        console.log(`[Layers] page_viewed (${reason}): ${window.location.pathname}`);
      }
    } catch (e) {
      if (client.enableDebug) console.warn('[Layers] page_viewed failed:', e);
    }
  }

  function flushPageleaveIfOpen() {
    if (!trackPageleave || !current) return;
    try {
      const elapsedMs = Math.max(0, Math.round(nowMs() - current.startedAtMs));
      const props: Record<string, unknown> = {
        pageview_id: current.pageviewId,
        window_id: windowId,
        url: current.url,
        time_on_page_ms: elapsedMs
      };
      client.track('page_left', props);
      if (client.enableDebug) {
        console.log(`[Layers] page_left time_on_page=${elapsedMs}ms`);
      }
    } catch (e) {
      if (client.enableDebug) console.warn('[Layers] page_left failed:', e);
    } finally {
      current = null;
    }
  }

  /** A tab that was hidden is visible again: the same page view continues. */
  function reopenIfClosed() {
    if (!trackPageleave || current || !last) return;
    current = { pageviewId: last.pageviewId, url: last.url, startedAtMs: nowMs() };
  }

  // ── Initial pageview ───────────────────────────────────────────────
  emitPageview('init');

  // ── popstate listener ──────────────────────────────────────────────
  // popstate fires for back/forward navigation in the same document.
  const onPopState = () => emitPageview('popstate');
  window.addEventListener('popstate', onPopState);

  // ── bfcache restore ────────────────────────────────────────────────
  // A page restored from the back/forward cache never re-runs init, so
  // the restore is the page view.
  const onPageshow = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) emitPageview('bfcache');
  };
  window.addEventListener('pageshow', onPageshow);

  // ── History API patches (pushState / replaceState) ─────────────────
  // Many SPAs use these directly without dispatching popstate. We patch
  // each method to still call the original implementation, then emit a
  // pageview only if the URL actually changed (some libraries call
  // replaceState with the same URL on every render). A runtime that has
  // frozen the History methods gets no patch at all: whatever was patched
  // before the failure is rolled back, and popstate still works.
  for (const method of HISTORY_PATCH_METHODS) {
    const original = window.history[method] as unknown as HistoryFn;
    const patched = function patchedHistory(this: History, ...args: unknown[]) {
      let before = '';
      let beforePage = '';
      try {
        before = locationKey();
        beforePage = pageKey();
      } catch {
        // Reading the location failed; the navigation itself still runs.
      }
      const result = original.apply(this, args as Parameters<HistoryFn>);
      if (active) {
        try {
          // `pushState` is a navigation whenever the URL changed at all.
          // `replaceState` is also how routers and search boxes mirror state
          // into the query string on every keystroke, so only a path or hash
          // change counts as a new page there.
          const changed =
            method === 'replaceState' ? pageKey() !== beforePage : locationKey() !== before;
          if (changed) emitPageview(method);
        } catch (e) {
          if (client.enableDebug) console.warn('[Layers] page view detection failed:', e);
        }
      }
      return result;
    };
    try {
      window.history[method] = patched as unknown as History[typeof method];
    } catch (e) {
      if (client.enableDebug) console.warn(`[Layers] could not patch history.${method}:`, e);
      restoreHistory();
      break;
    }
    originalHistory[method] = original;
    patchedHistory[method] = patched;
  }

  function restoreHistory() {
    for (const method of HISTORY_PATCH_METHODS) {
      const original = originalHistory[method];
      const ours = patchedHistory[method];
      if (original && ours) {
        try {
          // Another tool may have wrapped ours since; leave its wrapper in
          // place and let ours stay passive underneath.
          if ((window.history[method] as unknown) === ours) {
            window.history[method] = original as unknown as History[typeof method];
          }
        } catch {
          // Frozen since we patched it; nothing to restore.
        }
        delete originalHistory[method];
        delete patchedHistory[method];
      }
    }
  }

  // ── pageleave triggers ─────────────────────────────────────────────
  // Use both pagehide and visibilitychange:hidden — pagehide fires when
  // the document is being unloaded (or BFCache'd), visibilitychange fires
  // when the tab is hidden. Either is a good signal for "user is leaving
  // this page". We emit pageleave at most once per open page view, and a
  // tab that comes back re-opens the same one.
  const onPagehide = () => flushPageleaveIfOpen();
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      flushPageleaveIfOpen();
    } else if (document.visibilityState === 'visible') {
      reopenIfClosed();
    }
  };
  if (trackPageleave) {
    window.addEventListener('pagehide', onPagehide);
    document.addEventListener('visibilitychange', onVisibility);
  }
  // A `beforeunload` the user cancels leaves the document alive. The leave
  // is emitted (the client's own unload handler has usually done it already)
  // and the re-open is scheduled for the next task: a real unload never runs
  // it, a cancelled one does, and the same page view continues.
  const onBeforeUnload = () => {
    flushPageleaveIfOpen();
    setTimeout(reopenIfClosed, 0);
  };
  if (trackPageleave) {
    window.addEventListener('beforeunload', onBeforeUnload);
  }

  return {
    shutdown() {
      if (!active) return;
      active = false;
      // The client's shutdown is the user leaving this page as far as the
      // page view is concerned.
      flushPageleaveIfOpen();
      last = null;
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('pageshow', onPageshow);
      if (trackPageleave) {
        window.removeEventListener('pagehide', onPagehide);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('beforeunload', onBeforeUnload);
      }
      restoreHistory();
      delete marked[INSTALL_MARKER];
    },
    capturePageview() {
      emitPageview('manual');
    },
    capturePageleave() {
      flushPageleaveIfOpen();
    },
    currentPageviewId() {
      return (current ?? last)?.pageviewId;
    }
  };
}

// ── Helpers ──────────────────────────────────────────────────────────

function getOrCreateWindowId(): string {
  // sessionStorage scope is the tab — exactly the right granularity for
  // a per-tab window id. Survives soft reloads, lost on tab close.
  try {
    if (typeof sessionStorage !== 'undefined') {
      const existing = sessionStorage.getItem(SESSION_STORAGE_WINDOW_ID_KEY);
      if (existing) return existing;
      const id = generateUUID();
      sessionStorage.setItem(SESSION_STORAGE_WINDOW_ID_KEY, id);
      return id;
    }
  } catch {
    // sessionStorage may throw in private browsing or sandboxed iframes —
    // fall through to the in-memory id below.
  }
  return generateUUID();
}

function locationKey(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/** Path plus hash: the part of the URL a query-only `replaceState` leaves alone. */
function pageKey(): string {
  return `${window.location.pathname}${window.location.hash}`;
}

function parseQueryParams(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!search) return out;
  try {
    const params = new URLSearchParams(search);
    params.forEach((v, k) => {
      out[k] = v;
    });
  } catch {
    // ignore unparseable search strings
  }
  return out;
}

function safeUrlHost(url: string): string | null {
  try {
    return new URL(url).hostname || null;
  } catch {
    return null;
  }
}

function hasHighResClock(): boolean {
  return typeof performance !== 'undefined' && typeof performance.now === 'function';
}

function nowMs(): number {
  return hasHighResClock() ? performance.now() : Date.now();
}

// generateUUID is imported from ./uuid — the canonical implementation
// shared by index.ts and the pageview tracker.
